import { useEffect, useMemo, useState } from 'react';
import PageLayout from '../components/PageLayout';
import { useCompany } from '../context/CompanyContext';
import api, { downloadFile } from '../api';
import './Dashboard.css';

const CATEGORIES = ['Incorporation Docs', 'Tax Registrations', 'Licenses', 'Resolutions', 'Agreements', 'Other'];

const EXPIRY_PILL = {
  Active: 'dash-pill-green',
  'Expiring in 30 days': 'dash-pill-amber',
  Expired: 'dash-pill-red',
};

const EMPTY_UPLOAD = {
  client_id: '',
  document_name: '',
  document_category: 'Incorporation Docs',
  expiry_date: '',
  remarks: '',
  file: null,
};

function ClientSearchSelect({ clients, value, onChange, placeholder }) {
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const selected = clients.find((c) => c.id === value);

  const filtered = clients.filter((c) => c.name_of_client.toLowerCase().includes(query.toLowerCase()));

  return (
    <div className="client-search-select">
      <input
        className="field-input"
        placeholder={placeholder}
        value={open ? query : selected ? selected.name_of_client : ''}
        onFocus={() => {
          setOpen(true);
          setQuery('');
        }}
        onChange={(e) => setQuery(e.target.value)}
        onBlur={() => setOpen(false)}
      />
      {open && (
        <div className="client-search-dropdown" onMouseDown={(e) => e.preventDefault()}>
          {filtered.length === 0 && <div className="client-search-empty">No clients match</div>}
          {filtered.map((c) => (
            <button
              type="button"
              key={c.id}
              onClick={() => {
                onChange(c.id);
                setOpen(false);
              }}
            >
              {c.name_of_client}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function Documents() {
  const { selectedCompanyId, selectedCompany } = useCompany();
  const isBusinessOwner = selectedCompany?.client_mode === 'Business Owner';
  const [documents, setDocuments] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploadForm, setUploadForm] = useState(EMPTY_UPLOAD);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState(null);
  const [dragOver, setDragOver] = useState(false);

  const [filterClient, setFilterClient] = useState('');
  const [filterCategory, setFilterCategory] = useState('');
  const [search, setSearch] = useState('');

  const [editDoc, setEditDoc] = useState(null);
  const [editForm, setEditForm] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [toast, setToast] = useState(null);

  const clientName = (id) => clients.find((c) => c.id === id)?.name_of_client || '—';

  const refresh = () => {
    if (!selectedCompanyId) return Promise.resolve();
    const params = { company_id: selectedCompanyId };
    if (filterClient) params.client_id = filterClient;
    if (filterCategory) params.category = filterCategory;
    if (search) params.search = search;
    return api.get('/documents', { params }).then((res) => setDocuments(res.data));
  };

  useEffect(() => {
    if (!selectedCompanyId) return;
    api.get('/clients', { params: { company_id: selectedCompanyId } }).then((res) => setClients(res.data));
  }, [selectedCompanyId]);

  useEffect(() => {
    setLoading(true);
    refresh().finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCompanyId, filterClient, filterCategory, search]);

  const showToast = (msg) => {
    setToast(msg);
    setTimeout(() => setToast(null), 3000);
  };

  const handleFile = (file) => {
    setUploadForm((f) => ({ ...f, file }));
  };

  const handleUpload = (e) => {
    e.preventDefault();
    setUploadError(null);
    if (!selectedCompanyId) return;
    if ((!isBusinessOwner && !uploadForm.client_id) || !uploadForm.document_name || !uploadForm.file) {
      setUploadError(
        (isBusinessOwner ? '' : 'Client, ') + 'Document Name, and a file are all required.'
      );
      return;
    }
    setUploading(true);
    const fd = new FormData();
    fd.append('file', uploadForm.file);
    fd.append('company_id', selectedCompanyId);
    if (!isBusinessOwner && uploadForm.client_id) fd.append('client_id', uploadForm.client_id);
    fd.append('document_name', uploadForm.document_name);
    fd.append('document_category', uploadForm.document_category);
    if (uploadForm.expiry_date) fd.append('expiry_date', uploadForm.expiry_date);
    if (uploadForm.remarks) fd.append('remarks', uploadForm.remarks);

    api
      .post('/documents/upload', fd, { headers: { 'Content-Type': 'multipart/form-data' } })
      .then(() => {
        refresh();
        showToast('Document uploaded ✓');
        setUploadForm(EMPTY_UPLOAD);
      })
      .catch((err) => setUploadError(err.response?.data?.detail || 'Upload failed'))
      .finally(() => setUploading(false));
  };

  const openEdit = (doc) => {
    setEditDoc(doc);
    setEditForm({
      document_name: doc.document_name,
      document_category: doc.document_category || 'Other',
      expiry_date: doc.expiry_date || '',
      remarks: doc.remarks || '',
    });
  };

  const handleEditSubmit = (e) => {
    e.preventDefault();
    const payload = { ...editForm, expiry_date: editForm.expiry_date || null };
    api.put(`/documents/${editDoc.id}`, payload).then(() => {
      refresh();
      showToast('Document updated ✓');
      setEditDoc(null);
    });
  };

  const confirmDelete = () => {
    if (!deleteTarget) return;
    api.delete(`/documents/${deleteTarget.id}`).then(() => {
      refresh();
      showToast(`${deleteTarget.document_name} deleted`);
      setDeleteTarget(null);
    });
  };

  const handleDownload = (doc) => {
    const filename = doc.file_type ? `${doc.document_name}.${doc.file_type}` : doc.document_name;
    downloadFile(`/documents/${doc.id}/download`, filename).catch(() => showToast('Could not download file'));
  };

  const dropzoneLabel = useMemo(() => (uploadForm.file ? uploadForm.file.name : 'Drag & drop a file here, or click to browse'), [uploadForm.file]);

  return (
    <PageLayout>
      <div className="page-title-row">
        <h1 className="page-title">Document Depository</h1>
      </div>

      {toast && <div className="cal-toast">{toast}</div>}

      <div className="dash-card">
        <h2 className="dash-card-title">Upload New Document</h2>
        {uploadError && <p className="dash-error">{uploadError}</p>}
        <form onSubmit={handleUpload}>
          <div
            className={`doc-dropzone ${dragOver ? 'doc-dropzone-active' : ''}`}
            onDragOver={(e) => {
              e.preventDefault();
              setDragOver(true);
            }}
            onDragLeave={() => setDragOver(false)}
            onDrop={(e) => {
              e.preventDefault();
              setDragOver(false);
              if (e.dataTransfer.files?.[0]) handleFile(e.dataTransfer.files[0]);
            }}
            onClick={() => document.getElementById('doc-file-input').click()}
          >
            {dropzoneLabel}
            <input
              id="doc-file-input"
              type="file"
              style={{ display: 'none' }}
              onChange={(e) => e.target.files?.[0] && handleFile(e.target.files[0])}
            />
          </div>

          <div className="field-row">
            {!isBusinessOwner && (
              <div className="field-group">
                <span className="field-label">Client *</span>
                <ClientSearchSelect
                  clients={clients}
                  value={uploadForm.client_id}
                  onChange={(id) => setUploadForm((f) => ({ ...f, client_id: id }))}
                  placeholder="Search client..."
                />
              </div>
            )}
            <div className="field-group">
              <span className="field-label">Document Name *</span>
              <input
                className="field-input"
                value={uploadForm.document_name}
                onChange={(e) => setUploadForm({ ...uploadForm, document_name: e.target.value })}
                placeholder="e.g. GST Certificate"
              />
            </div>
          </div>

          <div className="field-row">
            <div className="field-group">
              <span className="field-label">Category</span>
              <select
                className="field-select"
                value={uploadForm.document_category}
                onChange={(e) => setUploadForm({ ...uploadForm, document_category: e.target.value })}
              >
                {CATEGORIES.map((c) => (
                  <option key={c} value={c}>{c}</option>
                ))}
              </select>
            </div>
            <div className="field-group">
              <span className="field-label">Expiry Date (optional)</span>
              <input
                type="date"
                className="field-input"
                value={uploadForm.expiry_date}
                onChange={(e) => setUploadForm({ ...uploadForm, expiry_date: e.target.value })}
              />
            </div>
          </div>

          <div className="field-group">
            <span className="field-label">Remarks</span>
            <input
              className="field-input"
              value={uploadForm.remarks}
              onChange={(e) => setUploadForm({ ...uploadForm, remarks: e.target.value })}
            />
          </div>

          <div className="dash-btn-row">
            <button className="dash-btn dash-btn-orange" type="submit" disabled={uploading}>
              {uploading ? 'Uploading...' : 'Upload Document'}
            </button>
          </div>
        </form>
      </div>

      <div className="dash-card">
        <div className="page-title-row">
          <h2 className="dash-card-title">Your Documents</h2>
        </div>

        <div className="field-row" style={{ marginBottom: 8 }}>
          {!isBusinessOwner && (
            <div className="field-group">
              <span className="field-label">Filter by Client</span>
              <select className="field-select" value={filterClient} onChange={(e) => setFilterClient(e.target.value)}>
                <option value="">All Clients</option>
                {clients.map((c) => (
                  <option key={c.id} value={c.id}>{c.name_of_client}</option>
                ))}
              </select>
            </div>
          )}
          <div className="field-group">
            <span className="field-label">Filter by Category</span>
            <select className="field-select" value={filterCategory} onChange={(e) => setFilterCategory(e.target.value)}>
              <option value="">All Categories</option>
              {CATEGORIES.map((c) => (
                <option key={c} value={c}>{c}</option>
              ))}
            </select>
          </div>
          <div className="field-group">
            <span className="field-label">Search by Name</span>
            <input
              className="field-input"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search documents..."
            />
          </div>
        </div>

        <table className="dash-table">
          <thead>
            <tr>
              <th>Document Name</th>
              {!isBusinessOwner && <th>Client</th>}
              <th>Category</th>
              <th>Upload Date</th>
              <th>Expiry Date</th>
              <th>Remarks</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {!loading && documents.length === 0 && (
              <tr>
                <td className="dash-empty" colSpan={isBusinessOwner ? 6 : 7}>No documents yet. Upload your first one above.</td>
              </tr>
            )}
            {documents.map((d) => (
              <tr key={d.id}>
                <td>{d.document_name}</td>
                {!isBusinessOwner && <td>{clientName(d.client_id)}</td>}
                <td>{d.document_category || '—'}</td>
                <td>{new Date(d.upload_date).toLocaleDateString('en-IN')}</td>
                <td>
                  {d.expiry_date || '—'}
                  {d.expiry_status && (
                    <span className={`dash-pill ${EXPIRY_PILL[d.expiry_status] || 'dash-pill-blue'}`} style={{ marginLeft: 6 }}>
                      {d.expiry_status}
                    </span>
                  )}
                </td>
                <td className="remarks-cell" title={d.remarks || ''}>{d.remarks || '—'}</td>
                <td>
                  <div className="table-actions">
                    <button className="dash-btn cal-btn-grey" type="button" onClick={() => handleDownload(d)}>
                      Download
                    </button>
                    <button className="dash-btn cal-btn-grey" type="button" onClick={() => openEdit(d)}>
                      Edit
                    </button>
                    <button className="dash-btn-red-outline" type="button" onClick={() => setDeleteTarget(d)}>
                      Delete
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {editDoc && (
        <div className="ncf-modal-overlay" onClick={() => setEditDoc(null)}>
          <div className="ncf-modal" onClick={(e) => e.stopPropagation()}>
            <h3 className="ncf-modal-title">Edit Document</h3>
            <form onSubmit={handleEditSubmit}>
              <div className="field-group">
                <span className="field-label">Document Name *</span>
                <input
                  className="field-input"
                  required
                  value={editForm.document_name}
                  onChange={(e) => setEditForm({ ...editForm, document_name: e.target.value })}
                />
              </div>
              <div className="field-row">
                <div className="field-group">
                  <span className="field-label">Category</span>
                  <select
                    className="field-select"
                    value={editForm.document_category}
                    onChange={(e) => setEditForm({ ...editForm, document_category: e.target.value })}
                  >
                    {CATEGORIES.map((c) => (
                      <option key={c} value={c}>{c}</option>
                    ))}
                  </select>
                </div>
                <div className="field-group">
                  <span className="field-label">Expiry Date</span>
                  <input
                    type="date"
                    className="field-input"
                    value={editForm.expiry_date || ''}
                    onChange={(e) => setEditForm({ ...editForm, expiry_date: e.target.value })}
                  />
                </div>
              </div>
              <div className="field-group">
                <span className="field-label">Remarks</span>
                <input
                  className="field-input"
                  value={editForm.remarks}
                  onChange={(e) => setEditForm({ ...editForm, remarks: e.target.value })}
                />
              </div>
              <p className="dash-table-sub">To replace the file itself, delete this document and upload it again.</p>
              <div className="ncf-modal-actions">
                <button type="button" className="dash-btn cal-btn-grey" onClick={() => setEditDoc(null)}>
                  Cancel
                </button>
                <button type="submit" className="dash-btn dash-btn-orange">
                  Save
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {deleteTarget && (
        <div className="ncf-modal-overlay" onClick={() => setDeleteTarget(null)}>
          <div className="ncf-modal" onClick={(e) => e.stopPropagation()}>
            <h3 className="ncf-modal-title">Delete Document</h3>
            <p style={{ fontSize: 14, color: '#374151' }}>
              Are you sure you want to delete <strong>{deleteTarget.document_name}</strong>? This will permanently
              delete the stored file. This cannot be undone.
            </p>
            <div className="ncf-modal-actions">
              <button className="dash-btn cal-btn-grey" type="button" onClick={() => setDeleteTarget(null)}>
                Cancel
              </button>
              <button className="dash-btn dash-btn-orange" style={{ background: '#dc2626' }} type="button" onClick={confirmDelete}>
                Yes, Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </PageLayout>
  );
}

export default Documents;
